/* eslint-disable */
import type { Money } from "../common/currency.pb";
import type { CurrencyCode } from "../common/currency_codes.pb";
import type { ExternalSystem } from "./external/external_system.pb";
import type { OctopusResult } from "./octopus.pb";
import type { Reward } from "./rewards.pb";

export enum TransactionState {
  /**
   * PENDING - The transaction has been initiated. If a card transaction, it has been authorized by us. A
   * transaction can have multiple pendings, but only one current pending.
   */
  PENDING = "PENDING",
  /** SETTLED - The money will move in the next batch. A single transaction can have multiple settlements. */
  SETTLED = "SETTLED",
  /**
   * VOIDED - The movement of money is canceled. However, the transaction could reauthorized. A single
   * transaction can have multiple voids.
   */
  VOIDED = "VOIDED",
  /** DECLINED - Money did not move, will never move, nor was anything pending. */
  DECLINED = "DECLINED",
  /** REFUNDED - The money moved and was sent back. A single transaction can have multiple refunds. */
  REFUNDED = "REFUNDED",
  /** REVERSED - A credit settlement amount was reversed. A single transaction can have only one reversal. */
  REVERSED = "REVERSED",
  /** DISPUTED - Chargeback that credited funds to this customer. */
  DISPUTED = "DISPUTED",
  /**
   * DISPUTE_REVERSED - The merchant disputed the chargeback(!) and now the money has moved back. This
   * ping-pong game between DISPUTED / DISPUTE_REVERSED can go on for some time until
   * it escalates to arbitration or someone gives up.
   */
  DISPUTE_REVERSED = "DISPUTE_REVERSED",
  /** DISPUTE_WON - The chargeback was won, final state. */
  DISPUTE_WON = "DISPUTE_WON",
  /**
   * IN_REVIEW - The transaction is in manual review - funds have not been released to the customer,
   * for e.g. unlinked refunds
   */
  IN_REVIEW = "IN_REVIEW",
  /**
   * IN_REVERSAL - The transaction is in the process of being reversed to an external system, e.g. a Bitcoin
   *  deposit that is unacceptable for risk reasons.
   */
  IN_REVERSAL = "IN_REVERSAL",
  /**
   * WAITING_ON_EXTERNAL - The transaction is in waiting for an asynchronous action - funds have not been released to the customer.
   * e.g. a Bitcoin deposit that is waiting for confirmations on blockchain or waiting for risk
   * automatic rule based decision.
   */
  WAITING_ON_EXTERNAL = "WAITING_ON_EXTERNAL",
  /**
   * SETTLED_EXTERNALLY - The money was moved to or from a product account, possibly via an
   * integration with a third party (e.g., incoming wire transfer via Modern
   * Treasury). The money is not settled in customer FBO account (yet?) - when
   * that happens, a 'SETTLED' event will be written.
   */
  SETTLED_EXTERNALLY = "SETTLED_EXTERNALLY",
  /**
   * REVERSED_EXTERNALLY - The money moved to/from a product account due to a transaction reversal.
   * The movement was possibly initiated by a third party (e.g., incoming wire
   * transfer reversal via Modern Treasury).
   */
  REVERSED_EXTERNALLY = "REVERSED_EXTERNALLY",
  /**
   * IN_REVERSAL_EXTERNALLY - The money is waiting to be reversed to/from a product account, possibly
   * via an integration with a third party (e.g., incoming wire transfer
   * reversal via Modern Treasury).
   */
  IN_REVERSAL_EXTERNALLY = "IN_REVERSAL_EXTERNALLY",
  /**
   * DISPUTE_WRITTEN_OFF - Transaction was disputed and the issuer (e.g., Cash) decided to take the
   * liability.
   */
  DISPUTE_WRITTEN_OFF = "DISPUTE_WRITTEN_OFF",
}

/**
 * This enum describes what kind of transaction this is. It is intended to
 * capture the originating reason for this transaction.
 */
export enum TransactionReasonCode {
  /** ATM_WITHDRAWAL - Cash withdrawn at an ATM. */
  ATM_WITHDRAWAL = "ATM_WITHDRAWAL",
  /** ATM_CHECK_DEPOSIT - A physical check deposited via ATM. */
  ATM_CHECK_DEPOSIT = "ATM_CHECK_DEPOSIT",
  /** ATM_FEE - An independent fee assessed by the ATM. */
  ATM_FEE = "ATM_FEE",
  /** CARD_PRESENT_PURCHASE - A virtual or physical card purchase at a brick-and-mortar store. */
  CARD_PRESENT_PURCHASE = "CARD_PRESENT_PURCHASE",
  /** CARD_NOT_PRESENT_PURCHASE - A virtual or physical card purchase by entering in the number. */
  CARD_NOT_PRESENT_PURCHASE = "CARD_NOT_PRESENT_PURCHASE",
  /** ACCOUNT_MAINTENANCE_FEE - A fee imposed by us to keep this account. */
  ACCOUNT_MAINTENANCE_FEE = "ACCOUNT_MAINTENANCE_FEE",
  /** BALANCE_ADJUSTMENT - A one-time arbitrary amount to correct an error or make Square/customer whole. */
  BALANCE_ADJUSTMENT = "BALANCE_ADJUSTMENT",
  /** SQUARE_CASH_SELF - Cash ins and outs. */
  SQUARE_CASH_SELF = "SQUARE_CASH_SELF",
  /** SQUARE_CASH_P2P - P2P payments sent or received. */
  SQUARE_CASH_P2P = "SQUARE_CASH_P2P",
  /** SQUARE_CASH_BUSINESS - Cash for business sent or received. */
  SQUARE_CASH_BUSINESS = "SQUARE_CASH_BUSINESS",
  /** PINDEBIT - Pindebit POS transaction */
  PINDEBIT = "PINDEBIT",
  /** PINCREDIT - Pincredit POS transaction (complement of pindebit). */
  PINCREDIT = "PINCREDIT",
  /** ACH - ACH deposit or withdrawal. */
  ACH = "ACH",
  /** UNLINKED_CREDIT - Usually use CARD_MERCHANT_REFUND instead. */
  UNLINKED_CREDIT = "UNLINKED_CREDIT",
  /**
   * GREENDOT_FEE - Obsolete.
   *
   * @deprecated
   */
  GREENDOT_FEE = "GREENDOT_FEE",
  /** NETWORK_FEE - A fee imposed by Visa, Shazam or MC */
  NETWORK_FEE = "NETWORK_FEE",
  /** SQUARE_CASH_FEE - A fee imposed by us (square cash). Nothing like this currently exists. */
  SQUARE_CASH_FEE = "SQUARE_CASH_FEE",
  /** CARD_MERCHANT_REFUND - An (usually unlinked) credit provided by a merchant via virtual or physical card */
  CARD_MERCHANT_REFUND = "CARD_MERCHANT_REFUND",
  /** OVERDRAFT_FEE - A fee imposed by us on overdrafting the account. Does not exist yet. */
  OVERDRAFT_FEE = "OVERDRAFT_FEE",
  /**
   * CARD_PRE_AUTHORIZATION - Unused.
   *
   * @deprecated
   */
  CARD_PRE_AUTHORIZATION = "CARD_PRE_AUTHORIZATION",
  /** MOBILE_CHECK_DEPOSIT - A check deposit via mobile. */
  MOBILE_CHECK_DEPOSIT = "MOBILE_CHECK_DEPOSIT",
  /** CHECK_PAYMENT - A physical check payment initiated from Cash App. */
  CHECK_PAYMENT = "CHECK_PAYMENT",
  /** CARD_VALIDATION - Usually a $0 purchase to validate that a card exists. */
  CARD_VALIDATION = "CARD_VALIDATION",
  /** ONLINE_PURCHASE - A non-token online purchase (implies CNP). */
  ONLINE_PURCHASE = "ONLINE_PURCHASE",
  /** ATM_BALANCE_CHECK - Non-money-moving transaction at ATM. */
  ATM_BALANCE_CHECK = "ATM_BALANCE_CHECK",
  /** SQUARE_REGISTER_INSTANT_DEPOSIT - Bottomless pull for Square Register ID. */
  SQUARE_REGISTER_INSTANT_DEPOSIT = "SQUARE_REGISTER_INSTANT_DEPOSIT",
  /** SQUARE_CASH_REFERRAL - Bottomless pull for Referral program. */
  SQUARE_CASH_REFERRAL = "SQUARE_CASH_REFERRAL",
  /** APPLE_PAY_PURCHASE - Payment made via apple pay. */
  APPLE_PAY_PURCHASE = "APPLE_PAY_PURCHASE",
  /** APPLE_PAY_ACTIVATION - Activation of an apple pay device. */
  APPLE_PAY_ACTIVATION = "APPLE_PAY_ACTIVATION",
  /** INTERNAL_CURRENCY_EXCHANGE - An internal transfer between balances owned by the same customer of different currencies. */
  INTERNAL_CURRENCY_EXCHANGE = "INTERNAL_CURRENCY_EXCHANGE",
  /** BITCOIN_TRANSFER - A bitcoin transfer from or to an external bitcoin wallet. */
  BITCOIN_TRANSFER = "BITCOIN_TRANSFER",
  /** ANDROID_PAY_PURCHASE - Payment made via android pay. */
  ANDROID_PAY_PURCHASE = "ANDROID_PAY_PURCHASE",
  /** ANDROID_PAY_ACTIVATION - Activation of an android pay device. */
  ANDROID_PAY_ACTIVATION = "ANDROID_PAY_ACTIVATION",
  /** PINDEBIT_CASHBACK - Cash withdrawn as cash back. */
  PINDEBIT_CASHBACK = "PINDEBIT_CASHBACK",
  /** PINDEBIT_QUASI_CASH - Cash-like transactions (ex. prepaid card reload). */
  PINDEBIT_QUASI_CASH = "PINDEBIT_QUASI_CASH",
  /** CARD_ACTIVATION - Activation of a the card itself. (book-keeping event generated internally) */
  CARD_ACTIVATION = "CARD_ACTIVATION",
  /** FUNDS_TRANSFER - Debit or credit to account using online funds transfer in or out of account e.g. Venmo ;) */
  FUNDS_TRANSFER = "FUNDS_TRANSFER",
  /** BILL_PAYMENT - Shazam bill payment */
  BILL_PAYMENT = "BILL_PAYMENT",
  /** ATM_UNVERIFIED_DEPOSIT - Envelope with unverified content deposited via ATM. */
  ATM_UNVERIFIED_DEPOSIT = "ATM_UNVERIFIED_DEPOSIT",
  /** ATM_CASH_DEPOSIT - Verified cash amount deposited via ATM. */
  ATM_CASH_DEPOSIT = "ATM_CASH_DEPOSIT",
  /** SAMSUNG_PAY_PURCHASE - Payment made via samsung pay. */
  SAMSUNG_PAY_PURCHASE = "SAMSUNG_PAY_PURCHASE",
  /** SAMSUNG_PAY_ACTIVATION - Activation of a samsung pay device. */
  SAMSUNG_PAY_ACTIVATION = "SAMSUNG_PAY_ACTIVATION",
  /** MICROSOFT_PAY_PURCHASE - Payment made via microsoft pay. */
  MICROSOFT_PAY_PURCHASE = "MICROSOFT_PAY_PURCHASE",
  /** MICROSOFT_PAY_ACTIVATION - Activation of a microsoft pay device. */
  MICROSOFT_PAY_ACTIVATION = "MICROSOFT_PAY_ACTIVATION",
  /** VISA_CHECKOUT_PURCHASE - Payment made via visa checkout. */
  VISA_CHECKOUT_PURCHASE = "VISA_CHECKOUT_PURCHASE",
  /** VISA_CHECKOUT_ACTIVATION - Activation of a visa checkout token. */
  VISA_CHECKOUT_ACTIVATION = "VISA_CHECKOUT_ACTIVATION",
  /** FACEBOOK_TOKEN_PURCHASE - Payment made via a facebook token. */
  FACEBOOK_TOKEN_PURCHASE = "FACEBOOK_TOKEN_PURCHASE",
  /** FACEBOOK_TOKEN_ACTIVATION - Activation of a facebook token. */
  FACEBOOK_TOKEN_ACTIVATION = "FACEBOOK_TOKEN_ACTIVATION",
  /** NETFLIX_TOKEN_PURCHASE - Payment made via a netflix token. */
  NETFLIX_TOKEN_PURCHASE = "NETFLIX_TOKEN_PURCHASE",
  /** NETFLIX_TOKEN_ACTIVATION - Activation of a netflix token. */
  NETFLIX_TOKEN_ACTIVATION = "NETFLIX_TOKEN_ACTIVATION",
  /** ATM_TRANSFER - Transfer between accounts at ATM */
  ATM_TRANSFER = "ATM_TRANSFER",
  /** BITCOIN_TRADE_SETTLEMENT - Transfer Bitcoin for trade. */
  BITCOIN_TRADE_SETTLEMENT = "BITCOIN_TRADE_SETTLEMENT",
  /** CAVIAR_SETTLEMENT - For Caviar courier payments */
  CAVIAR_SETTLEMENT = "CAVIAR_SETTLEMENT",
  /** DIGITAL_WALLET_PURCHASE - Payment made via a nonspecific digital wallet token. */
  DIGITAL_WALLET_PURCHASE = "DIGITAL_WALLET_PURCHASE",
  /** SECURITIES_TRADE - Securities trade, buy or sell * */
  SECURITIES_TRADE = "SECURITIES_TRADE",
  /** SECURITIES_DIVIDEND_PAYMENT - Securities dividend payment. * */
  SECURITIES_DIVIDEND_PAYMENT = "SECURITIES_DIVIDEND_PAYMENT",
  /** SECURITIES_CORPORATE_ACTION - Securities corporate action including split, reverse split, merger and acquisition. */
  SECURITIES_CORPORATE_ACTION = "SECURITIES_CORPORATE_ACTION",
  /** ACCOUNT_FUNDING_TRANSACTION - AFT transaction. Used to fund peer-to-peer money transfers. */
  ACCOUNT_FUNDING_TRANSACTION = "ACCOUNT_FUNDING_TRANSACTION",
  /** ORIGINAL_CREDIT_TRANSACTION - OCT credit. Used to push money to a debit card instantly. */
  ORIGINAL_CREDIT_TRANSACTION = "ORIGINAL_CREDIT_TRANSACTION",
  /** CUSTOMER_MERGE - A transaction debiting or crediting a balance due to customer merging. */
  CUSTOMER_MERGE = "CUSTOMER_MERGE",
  /**
   * LENDING_CASH_ADVANCE - A transaction debiting or crediting a balance due drawing down or repaying a loan.
   * Deprecated - please use one of the more-specific LOAN_* reason codes instead.
   *
   * @deprecated
   */
  LENDING_CASH_ADVANCE = "LENDING_CASH_ADVANCE",
  /** SECURITIES_TRADE_ERROR_CORRECTION - A transaction that is corrective of a previous SECURITIES_TRADE transaction. */
  SECURITIES_TRADE_ERROR_CORRECTION = "SECURITIES_TRADE_ERROR_CORRECTION",
  /** ADMIN_BALANCE_ADJUSTMENT - A transaction that results from an admin adjustment */
  ADMIN_BALANCE_ADJUSTMENT = "ADMIN_BALANCE_ADJUSTMENT",
  /** APPLE_PAY_NOT_PRESENT_PURCHASE - Payment made via apple pay where the customer was not present */
  APPLE_PAY_NOT_PRESENT_PURCHASE = "APPLE_PAY_NOT_PRESENT_PURCHASE",
  /** ANDROID_PAY_NOT_PRESENT_PURCHASE - Payment made via android pay where the customer was not present */
  ANDROID_PAY_NOT_PRESENT_PURCHASE = "ANDROID_PAY_NOT_PRESENT_PURCHASE",
  /** BITCOIN_P2P - Bitcoin peer to peer payment */
  BITCOIN_P2P = "BITCOIN_P2P",
  /** MONEY_ORDER - Transaction as a result of customer purchasing a money order */
  MONEY_ORDER = "MONEY_ORDER",
  /** PAPER_MONEY_DEPOSIT - Physical cash deposit at a retailer through a partner like VDBS */
  PAPER_MONEY_DEPOSIT = "PAPER_MONEY_DEPOSIT",
  /** PWC_PURCHASE - Transaction as a result of customer making a Pay With Cash purchase */
  PWC_PURCHASE = "PWC_PURCHASE",
  /** INSTANT_PAY_ADVANCE - Transaction as a result of a customer accessing their wages early */
  INSTANT_PAY_ADVANCE = "INSTANT_PAY_ADVANCE",
  /** INSTANT_PAY_PAYBACK - Transaction as a result of Instant Pay recovering funds that were advanced to the customer */
  INSTANT_PAY_PAYBACK = "INSTANT_PAY_PAYBACK",
  /** SPONSORED_CARD_ACTIVATION - Activation of a sponsored card. (book-keeping event generated internally) */
  SPONSORED_CARD_ACTIVATION = "SPONSORED_CARD_ACTIVATION",
  /** WIRE - WIRE deposit or withdrawal. */
  WIRE = "WIRE",
  /** LIGHTNING_TRANSFER - BTC Lightning Withdrawal. */
  LIGHTNING_TRANSFER = "LIGHTNING_TRANSFER",
  /** LENDING_DRAWDOWN - Drawdown - funds from a loan. */
  LENDING_DRAWDOWN = "LENDING_DRAWDOWN",
  /** LENDING_PAYMENT - A loan payment. */
  LENDING_PAYMENT = "LENDING_PAYMENT",
  /** LENDING_REFUND - Refund of a loan repayment. */
  LENDING_REFUND = "LENDING_REFUND",
  /** LENDING_LATE_FEE - A late fee applied to a loan. */
  LENDING_LATE_FEE = "LENDING_LATE_FEE",
  /** LENDING_INTEREST - An interest charge on a loan. */
  LENDING_INTEREST = "LENDING_INTEREST",
  /** LENDING_ADJUSTMENT - An adjustment to owing amount on a loan. */
  LENDING_ADJUSTMENT = "LENDING_ADJUSTMENT",
  /** CRYPTO_PAYROLL_CONVERSION - Crypto buy order that converts a percentage of fiat paycheck into crypto. */
  CRYPTO_PAYROLL_CONVERSION = "CRYPTO_PAYROLL_CONVERSION",
  /** CRYPTO_CASH_CARD_ROUND_UP - Crypto buy order that converts the round up amount of a fiat transaction into crypto. */
  CRYPTO_CASH_CARD_ROUND_UP = "CRYPTO_CASH_CARD_ROUND_UP",
  /** REMITTANCE_P2P_PAYMENT - An international remittance P2P transaction */
  REMITTANCE_P2P_PAYMENT = "REMITTANCE_P2P_PAYMENT",
  /** SAVINGS_TRANSFER - Savings Transfer transaction */
  SAVINGS_TRANSFER = "SAVINGS_TRANSFER",
  /** OVERDRAFT_LIMIT_UPDATE - Overdraft limit update transaction */
  OVERDRAFT_LIMIT_UPDATE = "OVERDRAFT_LIMIT_UPDATE",
  /** OVERDRAFT_REPAYMENT - Overdraft repayment transaction */
  OVERDRAFT_REPAYMENT = "OVERDRAFT_REPAYMENT",
  /** OVERDRAFT_NON_REPAYMENT_UPDATE - Overdraft non-repayment balance update transaction */
  OVERDRAFT_NON_REPAYMENT_UPDATE = "OVERDRAFT_NON_REPAYMENT_UPDATE",
  /** SQUARE_TAP_TO_PAY - Tap-to-Pay payment powered by Square */
  SQUARE_TAP_TO_PAY = "SQUARE_TAP_TO_PAY",
  /** OVERDRAFT_RECOUP_REPAYMENT - Overdraft cash in transaction for recouping funds */
  OVERDRAFT_RECOUP_REPAYMENT = "OVERDRAFT_RECOUP_REPAYMENT",
  /** OVERDRAFT_MANUAL_REPAYMENT - Overdraft manual repayment cash in transaction */
  OVERDRAFT_MANUAL_REPAYMENT = "OVERDRAFT_MANUAL_REPAYMENT",
}

/** The specific payment vector by which an issued instrument was used. */
export enum IssuedInstrumentVariant {
  CARD_PAN = "CARD_PAN",
  MOBILE_PHONE_TOKEN = "MOBILE_PHONE_TOKEN",
  WATCH_TOKEN = "WATCH_TOKEN",
  TABLET_TOKEN = "TABLET_TOKEN",
  LAPTOP_TOKEN = "LAPTOP_TOKEN",
  ACH_TRANSIT_NUMBER = "ACH_TRANSIT_NUMBER",
  STORED_VALUE_BALANCE = "STORED_VALUE_BALANCE",
  /**
   * PHYSICAL_CARD - Deprecated - use MAGSTRIPE, CHIP or CONTACTLESS instead
   *
   * @deprecated
   */
  PHYSICAL_CARD = "PHYSICAL_CARD",
  MAGSTRIPE = "MAGSTRIPE",
  CHIP = "CHIP",
  CONTACTLESS = "CONTACTLESS",
  UNKNOWN_INSTRUMENT_VARIANT = "UNKNOWN_INSTRUMENT_VARIANT",
}

export enum AccountType {
  /** PREPAID - Virtual Card Backed by Marqeta/Sutton */
  PREPAID = "PREPAID",
  /** STORED_VALUE - Regular Cash App Stored Value */
  STORED_VALUE = "STORED_VALUE",
  /** DDA - A fully functioning Direct Deposit Account (e.g. deposits and withdrawals) */
  DDA = "DDA",
  /**
   * LDDA - A Direct Deposit Account that provides a subset of features, when compared to
   * a DDA (e.g. only allows deposits)
   */
  LDDA = "LDDA",
}

export enum TransactionDeclineReason {
  /** UNKNOWN - This is a generic decline */
  UNKNOWN = "UNKNOWN",
  /** INSUFFICIENT_BALANCE - The balance would go negative if we authorized this purchase */
  INSUFFICIENT_BALANCE = "INSUFFICIENT_BALANCE",
  /**
   * POTENTIALLY_INSUFFICIENT_BALANCE - The Risk engine determined that the user's balance MAY go negative if other
   * pending authorizations are likely to capture more than their auth amount.
   */
  POTENTIALLY_INSUFFICIENT_BALANCE = "POTENTIALLY_INSUFFICIENT_BALANCE",
  /** BLOCKED_BY_RISK - The Risk engine determined that this purchase was likely fraudulent. */
  BLOCKED_BY_RISK = "BLOCKED_BY_RISK",
  /** CARD_DISABLED - The customer had disabled their card when this purchase was attempted. */
  CARD_DISABLED = "CARD_DISABLED",
  /**
   * INVALID_CURRENCY - Was declined because we do not support this currency either for risk or ledgering
   * reasons.
   */
  INVALID_CURRENCY = "INVALID_CURRENCY",
  /** RISK_SYSTEMS_UNAVAILABLE - Risk systems not available. */
  RISK_SYSTEMS_UNAVAILABLE = "RISK_SYSTEMS_UNAVAILABLE",
}

/**
 * When we get a dispute transaction advice we want to react differently to them depending on if
 * the merchant/acquirer challenges the dispute and we're instructed not to issue credits etc.
 */
export enum DisputeFundsAvailability {
  /** RELEASE_TO_CUSTOMER - When disputed funds are allowed to be issued. */
  RELEASE_TO_CUSTOMER = "RELEASE_TO_CUSTOMER",
  /**
   * WITHHOLD - When the dispute needs to be resolved, e.g. Marqeta reversing the dispute because they are
   * challenging it
   */
  WITHHOLD = "WITHHOLD",
  /** ISSUED - When disputed funds have already been issued. */
  ISSUED = "ISSUED",
  /** NOT_ISSUED - When disputed funds have NOT been issued. */
  NOT_ISSUED = "NOT_ISSUED",
}

/**
 * The event kind, currently only for pending events but extensible.
 * This field is specific to an individual event.
 */
export enum EventKind {
  /**
   * NO_AUTHORIZATION - For example, for single message transactions such as pindebits, where the PENDING
   * event is just for bookkeeping.
   */
  NO_AUTHORIZATION = "NO_AUTHORIZATION",
  /** AUTHORIZATION - This is a normal auth + capture style purchase. */
  AUTHORIZATION = "AUTHORIZATION",
  /** PREAUTHORIZATION - Uber-style funds hold. */
  PREAUTHORIZATION = "PREAUTHORIZATION",
  /** AFD_PREAUTHORIZATION - Automated Fuel Dispenser pre-authorization (gas-stations). */
  AFD_PREAUTHORIZATION = "AFD_PREAUTHORIZATION",
  /**
   * AUTH_ADJUSTMENT - Deprecated: Use AUTH_OVERRIDE instead.
   *
   * @deprecated
   */
  AUTH_ADJUSTMENT = "AUTH_ADJUSTMENT",
  /** ACQUIRER_FEE - An acquirer fee (ex. ATM Fee) that supplements a previous event. */
  ACQUIRER_FEE = "ACQUIRER_FEE",
  /** DISCOUNT - A discount applied to the transaction. */
  DISCOUNT = "DISCOUNT",
  /** SUBMITTED_TO_BITBANK - The transaction has been submitted Bitbank. */
  SUBMITTED_TO_BITBANK = "SUBMITTED_TO_BITBANK",
  /** SENT_TO_BLOCKCHAIN - The transaction has been submitted to the blockchain. */
  SENT_TO_BLOCKCHAIN = "SENT_TO_BLOCKCHAIN",
  /**
   * AUTH_OVERRIDE - Override to an earlier auth.  Will set the pending amount absolutely to the amount
   * specified as part of this transaction.
   */
  AUTH_OVERRIDE = "AUTH_OVERRIDE",
  /**
   * AUTH_INCREMENTAL - Adds to the amount of an earlier auth.
   * The original PENDING event will be VOIDED and a new PENDING event will be created with an
   * amount equal to this event's amount plus the original PENDING amount.
   */
  AUTH_INCREMENTAL = "AUTH_INCREMENTAL",
  /** MERCHANT_REVERSAL - A reversal/expiration initiated by the merchant. */
  MERCHANT_REVERSAL = "MERCHANT_REVERSAL",
  /** ISSUER_REVERSAL - A reversal/expiration initiated by the issuer (i.e. Marqeta). */
  ISSUER_REVERSAL = "ISSUER_REVERSAL",
  /**
   * BOOKKEEPING - An internal bookkeeping event, not originating from an external system
   * (e.g. a void before a settlement)
   */
  BOOKKEEPING = "BOOKKEEPING",
  /** CASH_APP_FEE - A fee (e.g. ATM fee) applied by CashApp */
  CASH_APP_FEE = "CASH_APP_FEE",
  /**
   * PARTIAL_CAPTURE - A capture has come through but it is not final, we should not release our auth on the remaining
   * funds and we should expect another capture to come through later to settle this transaction.
   */
  PARTIAL_CAPTURE = "PARTIAL_CAPTURE",
  /** CASH_BACK - A cash back that supplements a previous event. */
  CASH_BACK = "CASH_BACK",
  /** RECEIVED_FROM_BLOCKCHAIN - The transaction has been received from the blockchain. */
  RECEIVED_FROM_BLOCKCHAIN = "RECEIVED_FROM_BLOCKCHAIN",
  /** CASH_APP_FEE_REIMBURSEMENT - A cash app fee reimbursement applied to atm withdrawal. */
  CASH_APP_FEE_REIMBURSEMENT = "CASH_APP_FEE_REIMBURSEMENT",
  /** ACQUIRER_FEE_REIMBURSEMENT - An acquirer fee reimbursement applied to atm withdrawal. */
  ACQUIRER_FEE_REIMBURSEMENT = "ACQUIRER_FEE_REIMBURSEMENT",
  /** SETTLED_INTERNALLY - An internally-created settlement event. */
  SETTLED_INTERNALLY = "SETTLED_INTERNALLY",
  /**
   * EARLY_DIRECT_DEPOSIT - A direct deposit, or other kind of credit transaction event that hasn't been fully
   * settled/processed yet, but the funds are available to the customer (transaction is prefunded).
   */
  EARLY_DIRECT_DEPOSIT = "EARLY_DIRECT_DEPOSIT",
  /** PROVISIONAL_CREDIT_ISSUED - A chargeback where a provisional credit was issued. */
  PROVISIONAL_CREDIT_ISSUED = "PROVISIONAL_CREDIT_ISSUED",
  /** PROVISIONAL_CREDIT_WITHHELD - A chargeback where a provisional credit was withheld. */
  PROVISIONAL_CREDIT_WITHHELD = "PROVISIONAL_CREDIT_WITHHELD",
  /**
   * PARTIAL_AUTH - An authorize for a lesser amount than the requested amount.
   * Typically used for AFD (automated fuel dispensing) or for split tender use cases.
   */
  PARTIAL_AUTH = "PARTIAL_AUTH",
  PAPER_CASH_DEPOSIT = "PAPER_CASH_DEPOSIT",
  /** GOVERNMENT_FEE - Any Goverment fee charged in the transaction that's not related to taxes. */
  GOVERNMENT_FEE = "GOVERNMENT_FEE",
  /** TAX_WITHHOLDING - Tax withholding for customers who have been flag by Goverment Revenue Service. */
  TAX_WITHHOLDING = "TAX_WITHHOLDING",
  /** LOAN_PRINCIPAL - Loan principal. */
  LOAN_PRINCIPAL = "LOAN_PRINCIPAL",
  /** LOAN_INTEREST - Loan interest. */
  LOAN_INTEREST = "LOAN_INTEREST",
  /** LOAN_SETUP_FEE - Loan setup fees. */
  LOAN_SETUP_FEE = "LOAN_SETUP_FEE",
  /** LOAN_LATE_FEE - Loan late fees. */
  LOAN_LATE_FEE = "LOAN_LATE_FEE",
}

export enum TransactionReferenceType {
  /** PURCHASE_CONTRACT_TOKEN - Token of purchase contract whose execution created this transaction. */
  PURCHASE_CONTRACT_TOKEN = "PURCHASE_CONTRACT_TOKEN",
  /** BLOCKCHAIN_TRANSACTION_ID - Blockchain transaction id of blockchain transaction associated with this banklin transaction . */
  BLOCKCHAIN_TRANSACTION_ID = "BLOCKCHAIN_TRANSACTION_ID",
  /** MARQETA_DIRECT_DEPOSIT_TOKEN - Direct deposit token as issued by Marqeta - separate from the transaction token. */
  MARQETA_DIRECT_DEPOSIT_TOKEN = "MARQETA_DIRECT_DEPOSIT_TOKEN",
  /** CRYPTO_ORDER_TOKEN - Token of invest crypto order associated with this banklin transaction. */
  CRYPTO_ORDER_TOKEN = "CRYPTO_ORDER_TOKEN",
}

export interface Transaction {
  /** Unique token representing this transaction. */
  token?: string;
  /** Current state of this transaction. */
  state?: TransactionState;
  /** The external system where this event was originated. */
  external_system?: ExternalSystem;
  /** The token of the balance affected by this transaction. */
  balance_token?: string;
  /** The token of the instrument associated with this transaction, if any. */
  instrument_token?: string;
  /**
   * The amount by which this transaction has currently affected the stored value balance.
   *
   * This amount changes as new events are added to this transaction.
   */
  amount?: Money;
  /** Timestamp when this transaction was initiated. */
  pending_at?: number;
  /** Timestamp of the latest settlement for this transaction. */
  settled_at?: number;
  /** Timestamp when this transaction was voided. */
  voided_at?: number;
  /** Timestamp when this transaction was declined. */
  declined_at?: number;
  /** Timestamp of the latest refund for this transaction. */
  refunded_at?: number;
  description?: string;
  reference?: string;
  /** @deprecated */
  decline_reason?: TransactionDeclineReason;
  transaction_reason_code?: TransactionReasonCode;
  /**
   * Type of stored value account used in transaction.
   * If the amount is negative, the stored value is being pulled from.
   * If the amount is positive, the stored value is being pushed to.
   *
   * @deprecated
   */
  account_type?: AccountType;
  /** The vector along which the issued instrument was used to make this transaction */
  instrument_variant?: IssuedInstrumentVariant;
  /** Token representing the merchant who initiated this payment */
  merchant_token?: string;
  /** The initial pending amount of the transaction */
  pending_amount?: Money;
  /** The time the transaction record was created, in millis */
  created_at?: number;
  /** The time the transaction record was last updated, in millis */
  updated_at?: number;
  /** The last pending amount of the transaction */
  last_pending_amount?: Money;
}

export interface TransactionEvent {
  /** Unique token representing this transaction event. */
  token?: string;
  /** Token representing a transaction that is represented by this event. */
  transaction_token?: string;
  /** This event type. */
  type?: TransactionState;
  /** The external system where this event was originated. */
  external_system?: ExternalSystem;
  /** The ID of this transaction event on the external partner system. */
  external_system_id?: string;
  /** The token of the balance affected by this transaction. */
  balance_token?: string;
  /** The token of the instrument associated with this transaction if any. */
  instrument_token?: string;
  /** occured at timestamp in milliseconds * */
  occurred_at?: number;
  /** The sequence of this event in it's owning transaction. */
  event_order?: number;
  /** How much this event will affect the customer balance. */
  amount?: Money;
  /** A snapshot of the stored value balance right after it was affected by this event. */
  balance_snapshot_amount?: Money;
  /** Usually the merchant name or statement description */
  description?: string;
  /** A customer-visible reference code for tracking */
  reference?: string;
  /**
   * If event type is DECLINE, the reason for the decline
   *
   * @deprecated
   */
  decline_reason?: TransactionDeclineReason;
  /** A snapshot of the stored value balance version right after it was affected by this event. */
  balance_snapshot_version?: number;
  /** The unified result for this transaction event. */
  octopus_result?: OctopusResult;
  /** The reason code for the transaction. Such as APPLE_PAY_PURCHASE. */
  reason_code?: TransactionReasonCode;
  /**
   * If a PENDING event, then what type of authorization it is (e.g. gas station pre-auth).
   * Remains null for other event types, currently. Note that this is specific to an individual
   * event and may even be different for multiple PENDING events within the same transaction.
   *
   * If a PENDING or REFUNDED event, is set to DISCOUNT for reward payments.
   */
  kind?: EventKind;
  /** Present when kind is DISCOUNT. */
  discount?:
    | TransactionEventDiscount
    | undefined;
  /** Token representing the merchant who initiated this payment */
  merchant_token?: string;
  /** If a PENDING event, when it expires. */
  expires_at?: number;
  /** Token representing the customer who initiated this transaction. */
  customer_token?: string;
  /** The ID of the preceding related transaction on the external partner system. */
  preceding_related_external_system_id?: string;
  dispute_funds_availability?: DisputeFundsAvailability;
  /** The withdrawal fee paid by the customer for BTC transfers */
  customer_fee_satoshis?: number;
  /** The target wallet address for BTC withdrawals */
  target_wallet_address?: string;
}

export interface TransactionEventDiscount {
  reward?:
    | Reward
    | undefined;
  /** soon to be deprecated */
  amount?: Money;
  /**
   * The actual discount amount after apply the discount.
   * example 1: reward_amount = $1 then discount_amount = $1
   * example 2: reward_basis_point = 100 and transaction_amount is $200 then discount_amount = $2
   */
  discount_amount?: Money;
  reward_discount?: RewardDiscount;
}

export interface RewardDiscount {
  discount_token?: string;
  reward_amount?: Money | undefined;
  reward_basis_points?:
    | number
    | undefined;
  /**
   * If the reward discount came as a result of a reimbursement, this will be populated with
   * the token of the approved missing reward report; otherwise it will be null.
   *
   * @deprecated
   */
  missing_reward_report_token?: string;
  missing_reward_report_data?: RewardDiscountMissingRewardReportData;
  /**
   * Abandoning the following fields in favor of a single packaged CrossCurrencyRewardInfo
   *
   * @deprecated
   */
  cross_currency_reward?: Money;
  /** @deprecated */
  cross_currency_reward_exchange_rate?: Money;
  /** @deprecated */
  card_transaction_token?: string;
  /** @deprecated */
  reward_transaction_token?: string;
  cross_currency_reward_info?: RewardDiscountCrossCurrencyRewardInfo;
}

export interface RewardDiscountMissingRewardReportData {
  missing_reward_report_token?: string;
  approved_once?: boolean;
  approval_reason?: RewardDiscountMissingRewardReportDataApprovalReason;
}

export enum RewardDiscountMissingRewardReportDataApprovalReason {
  /**
   * MERCHANT_DATA_UPDATED - Merchant information was updated which resulted in the merchant becoming eligible for boost
   * at the time the transaction was being executed.
   */
  MERCHANT_DATA_UPDATED = "MERCHANT_DATA_UPDATED",
  ONE_TIME_APPROVAL_SILENT = "ONE_TIME_APPROVAL_SILENT",
  ONE_TIME_APPROVAL = "ONE_TIME_APPROVAL",
}

/**
 * Populated only if this payment is a boost where the reward is a different currency than the
 * card transaction.  Gives us context related to the reward transaction for display on the card
 * transaction.
 */
export interface RewardDiscountCrossCurrencyRewardInfo {
  /** Amount cents rewarded in the reward_currency. */
  reward_amount?: number;
  /** The currency of this reward. */
  reward_currency?: CurrencyCode;
  /** The card currency value of this reward at the time it is granted. */
  reward_value_snapshot?: Money;
  /**
   * Exchange rate used to buy the reward.
   * Amount here is denoted in the card currency amount and is understood to be
   * that is the exchange rate per whole reward_currency dollars.
   * For example, if this reward_exchange_rate is Money.of(100_00L, USD), and the reward_currency
   * is BTC, the exchange rate is 10000 usd cents per 1 BTC, or $100 per Bitcoin.
   */
  reward_exchange_rate?: Money;
  /**
   * StoredValueBalanceTransactionToken populated for cross currency rewards, provides a pointer to
   * the reward transaction when the reward is in a different currency than the card transaction.
   */
  reward_transaction_token?: string;
  /** Token of card transaction that triggered this boost. */
  card_transaction_token?: string;
}

export interface TransactionReference {
  /** Id of DbTransaction this reference is for. */
  transaction_id?: string;
  /** Reference token associated with this transaction. */
  reference_token?: string;
  /** Type of reference_token. */
  type?: TransactionReferenceType;
  /** Timestamp that this reference was created. */
  created_at?: number;
  /** Timestamp that this reference was updated at. */
  updated_at?: number;
}
